// Mock implementation of the PE serving-layer contract.
//
// Deterministic: the same (chapter_id, tier, language) always yields the same
// scenes and items, so screens render stably across reloads. An in-memory HITL
// queue is seeded with a few pending audio items and is also fed by any audio
// submission made through `submitItem`, so the HITL screen has live data to act
// on during development.
//
// This file contains NO Supabase/network calls. Flipping to the live backend is
// a one-line change in `index.ts`.

import type {
  AssessmentItem,
  AssessmentsResponse,
  ContentKey,
  HitlItem,
  HitlQueueQuery,
  HitlQueueResponse,
  ItemType,
  Lang,
  Materials,
  ReleaseInput,
  ReleaseResult,
  ResponseMode,
  Scene,
  ScenesResponse,
  ServingApi,
  SubmitInput,
  SubmitResult,
  Tier,
} from "./types";

// --- small deterministic helpers -------------------------------------------

/** Stable string hash → non-negative int (FNV-1a). Lets mocks vary by key without Math.random. */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

const SIM_LATENCY_MS = 220;
function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), SIM_LATENCY_MS));
}

/** Addressable item id per the contract: `{source_id}-{lang}`. */
export function itemId(sourceId: string, lang: Lang): string {
  return `${sourceId}-${lang}`;
}

// --- scenes ------------------------------------------------------------------

function buildScenes(key: ContentKey): Scene[] {
  const isEs = key.language === "es";
  const scenes: Scene[] = [];
  for (let n = 1; n <= 20; n++) {
    scenes.push({
      scene_code: `1-CH?-SC${String(n).padStart(2, "0")}`,
      scene_number: n,
      prose: isEs
        ? `Escena ${n}: Layla y sus amigos exploran la parte ${n} del capítulo. (Narración ${key.tier}, ES.)`
        : `Scene ${n}: Layla and her friends explore part ${n} of the chapter. (${key.tier} narration, EN.)`,
      image_url: null,
      audio_url: null, // mock has no audio assets
      timing_url: null,
    });
  }
  return scenes;
}

// --- materials ---------------------------------------------------------------

function buildMaterials(chapterId: string, language: Lang): Materials {
  const isEs = language === "es";
  return {
    chapter_id: chapterId,
    language,
    observation_checklist: {
      source_id: `ch${chapterId}-obs`,
      material_type: "observation_checklist",
      language,
      title: isEs
        ? `Lista de observación — Capítulo ${chapterId}`
        : `Observation Checklist — Chapter ${chapterId}`,
      behaviors: [1, 2, 3, 4, 5].map((i) => ({
        text: isEs
          ? `El estudiante demuestra el comportamiento observable ${i} durante la escena.`
          : `Student demonstrates observable behavior ${i} during the scene.`,
        scene_ref: i * 3, // anchored to a specific scene (WAVE_A locked rule)
      })),
      asset_url: null,
    },
    family_letter: {
      source_id: `ch${chapterId}-fam`,
      material_type: "family_letter",
      language,
      title: isEs
        ? `Carta para la familia — Capítulo ${chapterId}`
        : `Family Letter — Chapter ${chapterId}`,
      body: isEs
        ? `Estimada familia,\n\nEsta semana su hijo/a aprendió ideas importantes en el Capítulo ${chapterId}. Pregúntele qué descubrió Layla y pídale que le cuente su parte favorita. En casa, pueden conversar sobre las palabras nuevas durante la cena.\n\nGracias por explorar junto a nosotros.`
        : `Dear Family,\n\nThis week your child explored big ideas in Chapter ${chapterId}. Ask them what Layla discovered and have them tell you their favorite part. At home, you can echo this chapter's new words together at dinner.\n\nThank you for exploring alongside us.`,
      asset_url: null,
    },
  };
}

// --- assessment item composition --------------------------------------------

/**
 * Tier composition rules (locked):
 *   T1 = audio_match / audio
 *   T2 = mc / text (text == sentence_builder; never open_response)
 *   T3 = ≥ 2 types
 *   T4 = includes 2× sentence_builder + 1× open_response
 * open_response is never rendered at T1/T2.
 */
const TIER_TYPE_PLAN: Record<Tier, ItemType[]> = {
  // 12 items each
  T1: [
    "audio_match", "audio", "audio_match", "audio", "audio_match", "audio",
    "audio_match", "audio", "audio_match", "audio", "audio_match", "audio",
  ],
  T2: [
    "mc", "sentence_builder", "mc", "sentence_builder", "mc", "sentence_builder",
    "mc", "sentence_builder", "mc", "sentence_builder", "mc", "mc",
  ],
  T3: [
    "mc", "sentence_builder", "audio_match", "mc", "open_response", "audio",
    "mc", "sentence_builder", "audio_match", "mc", "open_response", "mc",
  ],
  T4: [
    "sentence_builder", "sentence_builder", "open_response", "mc", "audio",
    "audio_match", "mc", "sentence_builder", "open_response", "mc", "audio", "mc",
  ],
};

/** auto-score eligible except recorded-audio answers, which route to HITL. */
function responseModeFor(type: ItemType): ResponseMode {
  return type === "audio" ? "audio" : "auto";
}

function buildItem(key: ContentKey, index: number, type: ItemType): AssessmentItem {
  const sourceId = `ch${key.chapterId}-${key.tier}-q${String(index + 1).padStart(2, "0")}`;
  const sceneNo = ((hash(sourceId) % 20) + 1);
  const isEs = key.language === "es";
  const base: AssessmentItem = {
    source_id: sourceId,
    item_id: itemId(sourceId, key.language),
    chapter_id: key.chapterId,
    scene_id: `ch${key.chapterId}-sc${String(sceneNo).padStart(2, "0")}`,
    tier: key.tier,
    language: key.language,
    type,
    response_mode: responseModeFor(type),
    prompt: "",
  };

  switch (type) {
    case "mc":
      return {
        ...base,
        prompt: isEs
          ? `¿Qué sucede en la escena ${sceneNo}?`
          : `What happens in scene ${sceneNo}?`,
        choices: ["A", "B", "C", "D"].map((id) => ({
          id,
          text: isEs ? `Opción ${id}` : `Choice ${id}`,
          audio_url: null,
        })),
      };
    case "audio_match":
      return {
        ...base,
        prompt: isEs
          ? `Escucha y elige la respuesta que corresponde a la escena ${sceneNo}.`
          : `Listen and choose the answer that matches scene ${sceneNo}.`,
        choices: ["A", "B", "C"].map((id) => ({
          id,
          text: isEs ? `Audio ${id}` : `Audio ${id}`,
          audio_url: `mock://audio/${sourceId}-${id}.mp3`,
        })),
      };
    case "audio":
      return {
        ...base,
        prompt: isEs
          ? `Graba tu respuesta hablada sobre la escena ${sceneNo}.`
          : `Record your spoken answer about scene ${sceneNo}.`,
        audio_prompt_url: `mock://audio/${sourceId}-prompt.mp3`,
      };
    case "sentence_builder":
      return {
        ...base,
        prompt: isEs
          ? `Ordena las palabras para describir la escena ${sceneNo}.`
          : `Arrange the words to describe scene ${sceneNo}.`,
        word_bank: isEs
          ? ["Layla", "explora", "una", "idea", "nueva"]
          : ["Layla", "explores", "a", "new", "idea"],
      };
    case "open_response":
      return {
        ...base,
        prompt: isEs
          ? `Escribe con tus palabras qué aprendiste en la escena ${sceneNo}.`
          : `In your own words, write what you learned in scene ${sceneNo}.`,
      };
  }
}

/**
 * Guard the locked rule defensively: open_response must never be served at
 * T1/T2. Exported so the screen can also assert before rendering.
 */
export function enforceTierComposition(
  tier: Tier,
  items: AssessmentItem[]
): AssessmentItem[] {
  if (tier === "T1" || tier === "T2") {
    return items.filter((it) => it.type !== "open_response");
  }
  return items;
}

function buildAssessments(key: ContentKey): AssessmentItem[] {
  const plan = TIER_TYPE_PLAN[key.tier];
  const items = plan.map((type, i) => buildItem(key, i, type));
  return enforceTierComposition(key.tier, items);
}

// --- in-memory HITL queue ----------------------------------------------------

const STUDENT_NAMES = ["Mateo R.", "Aisha K.", "Liam P.", "Sofía G.", "Noah T."];
let queueSeq = 0;
const hitlQueue: HitlItem[] = [];

function nextQueueId(): string {
  queueSeq += 1;
  return `q-${String(queueSeq).padStart(4, "0")}`;
}

// Seed a few pending audio items so the HITL screen has data immediately.
function seedQueue() {
  if (hitlQueue.length > 0) return;
  const seeds: Array<{ chapterId: string; tier: Tier; lang: Lang }> = [
    { chapterId: "1", tier: "T1", lang: "en" },
    { chapterId: "1", tier: "T3", lang: "es" },
    { chapterId: "2", tier: "T4", lang: "en" },
  ];
  seeds.forEach((s, i) => {
    const sourceId = `ch${s.chapterId}-${s.tier}-q01`;
    hitlQueue.push({
      queue_item_id: nextQueueId(),
      item_id: itemId(sourceId, s.lang),
      source_id: sourceId,
      chapter_id: s.chapterId,
      tier: s.tier,
      language: s.lang,
      student_name: STUDENT_NAMES[i % STUDENT_NAMES.length],
      prompt:
        s.lang === "es"
          ? "Graba tu respuesta hablada sobre la escena."
          : "Record your spoken answer about the scene.",
      response_mode: "audio",
      audio_url: `mock://audio/submission-${sourceId}.webm`,
      response_text: null,
      submitted_at: `2026-06-0${(i % 9) + 1}T14:0${i}:00Z`,
      status: "awaiting_review",
      ai_preliminary_score: 3 + (i % 2),
      ai_score_fallback: false,
      ai_preliminary_feedback:
        "AI preliminary: clear pronunciation, on-topic. Teacher review required before release.",
      max_score: 5,
    });
  });
}
seedQueue();

// --- the mock api ------------------------------------------------------------

export const mockServingApi: ServingApi = {
  getScenes(key: ContentKey): Promise<ScenesResponse> {
    const scenes = buildScenes(key);
    return delay({
      chapter_id: key.chapterId,
      tier: key.tier,
      language: key.language,
      count: scenes.length,
      timing_url: null,
      scenes,
    });
  },

  getMaterials({ chapterId, language }): Promise<Materials> {
    return delay(buildMaterials(chapterId, language));
  },

  getChapterStandards(chapterId: string) {
    // Mocks ship no standards fixtures — return the empty-state shape.
    return delay({ chapterId: Number(chapterId), frameworks: [] });
  },

  getAssessments(key: ContentKey): Promise<AssessmentsResponse> {
    return delay({
      chapter_id: key.chapterId,
      tier: key.tier,
      language: key.language,
      items: buildAssessments(key),
    });
  },

  // Summative chapter quiz (T3/T4). Reuses the formative item builder since
  // quizzes are takeable items of the same shapes; the live layer hits /pe/quiz.
  getQuiz(key: ContentKey): Promise<AssessmentsResponse> {
    return delay({
      chapter_id: key.chapterId,
      tier: key.tier,
      language: key.language,
      items: buildAssessments(key),
    });
  },

  submitItem(input: SubmitInput): Promise<SubmitResult> {
    // Audio answers route to the HITL queue — never auto-scored, never released.
    if (input.response_mode === "audio") {
      const queueItemId = nextQueueId();
      hitlQueue.push({
        queue_item_id: queueItemId,
        item_id: input.item_id,
        source_id: input.source_id,
        chapter_id: input.chapter_id,
        tier: input.tier,
        language: input.language,
        student_name: "You (this session)",
        prompt: "Recorded audio answer.",
        response_mode: "audio",
        audio_url: input.audio
          ? URL.createObjectURL(input.audio)
          : `mock://audio/submission-${input.source_id}.webm`,
        response_text: null,
        submitted_at: "2026-06-02T00:00:00Z",
        status: "awaiting_review",
        ai_preliminary_score: null,
        ai_score_fallback: false,
        ai_preliminary_feedback: null,
        max_score: 5,
      });
      return delay({
        item_id: input.item_id,
        response_mode: "audio",
        status: "queued_for_review",
        score: null,
        max_score: 5,
        is_correct: null,
        feedback: "Your spoken answer was sent to your teacher for review.",
        queue_item_id: queueItemId,
        is_grade_released: false,
      });
    }

    // Auto-scored types. mc / audio_match check the selected choice; text types
    // accept any non-empty response (open_response gets an AI-preliminary score
    // that still requires teacher release before it counts).
    let isCorrect: boolean | null;
    const maxScore = 1;
    if (input.type === "mc" || input.type === "audio_match") {
      // Deterministic "correct" choice for the mock: derived from source_id.
      const correct = ["A", "B", "C", "D"][hash(input.source_id) % 4];
      isCorrect = input.choice_id === correct;
    } else {
      // sentence_builder / open_response: credit a non-empty response.
      isCorrect = (input.text ?? "").trim().length > 0;
    }
    const score = isCorrect ? 1 : 0;

    return delay({
      item_id: input.item_id,
      response_mode: "auto",
      status: "auto_scored",
      score,
      max_score: maxScore,
      is_correct: isCorrect,
      feedback:
        isCorrect === true
          ? "Correct!"
          : isCorrect === false
          ? "Not quite — review the scene and try again."
          : null,
      queue_item_id: null,
      // open_response is preliminary-only; nothing reaches the gradebook here.
      is_grade_released: false,
    });
  },

  releaseScore(input: ReleaseInput): Promise<ReleaseResult> {
    const entry = hitlQueue.find((q) => q.queue_item_id === input.queue_item_id);
    if (entry) {
      entry.status = "released";
      entry.ai_preliminary_score = input.score;
    }
    return delay({
      queue_item_id: input.queue_item_id,
      status: "released",
      released_score: input.score,
      is_grade_released: true,
    });
  },

  getHitlQueue(query: HitlQueueQuery): Promise<HitlQueueResponse> {
    let items = hitlQueue.filter((q) => q.status !== "released");
    if (query.chapterId) {
      items = items.filter((q) => q.chapter_id === query.chapterId);
    }
    return delay({ items: items.map((q) => ({ ...q })) });
  },

  // WS2 Rider: ai_auto_release is plumbing only; mock returns the default-off state.
  getTeacherSettings() {
    return delay({ ai_auto_release: false, retake_policy: "latest_replaces" as const });
  },
};
